import { HttpClient, HttpResourceRef } from '@angular/common/http';
import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, EMPTY, filter, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../../models/user.model';
import { API_ENDPOINT, USE_BACKEND_AVATAR_API } from '../constants/app.constants';
import { UserPermission, userPermissionFromGroupId } from '../constants/permission.constants.enum';
import { UserFacilityPermission } from '../interfaces/lookup.interface';
import { HomeService } from './home.service';
import { MicrosoftGraphService } from './microsoft-graph.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  displayName = signal('');
  name = signal('');
  id = signal('');
  username = signal('');
  jobTitle = signal('');
  userPrincipalName = signal('');
  userId = signal<number>(1);
  firstName = signal<string | undefined>(undefined);
  lastName = signal<string | undefined>(undefined);
  isActive = signal(false);
  facilityIds = signal<number[]>([]);
  facilityPermissions = signal<UserFacilityPermission[]>([]);
  profileLoaded = signal(false);
  photo: WritableSignal<string | undefined> = signal(undefined);
  authenticated = signal<boolean>(false);
  permissions = signal<UserPermission[]>([]);
  hasNoPermissions = signal<boolean>(false);
  permissionsLoaded = signal<boolean>(false);
  permission$:Observable<UserPermission[]>;
  hasNoPermissions$: Observable<boolean>;

  userInfoUri = environment.apiConfig.apiEndpointBase + API_ENDPOINT.USER_INFO;
  avatarUri = environment.apiConfig.apiEndpointBase + API_ENDPOINT.AVATAR;

  userEmailToFetch = signal<string | null>(null);
  userProfileResource!: HttpResourceRef<{ data: User, isError: boolean, message: string } | undefined>;

  profileSettled!: Signal<boolean>;

  constructor(
    private authService: MsalService,
    private graphService: MicrosoftGraphService,
    private homeService: HomeService,
    private http: HttpClient,
    private router: Router
  ) {
    effect(() => {
      const permissions = this.permissions();
      if (this.permissionsLoaded()) {
        console.log(`user permission Ids: ${permissions}`);
        // Check if any of the fetched permissions are valid with UserPermission enum values
        const hasAnyValidPermission = permissions.some(p => Object.values(UserPermission).includes(p));

        if (hasAnyValidPermission) {
          this.hasNoPermissions.set(false);
        } else {
          this.hasNoPermissions.set(true);
          this.authenticated.set(false);
          this.router.navigate(['/not-authorized']);
        }
      } else {
        console.log('Permissions are still being loading...');
      }
    });
    this.permission$ = toObservable(this.permissions).pipe(
      filter((p) => p.length > 0)
    );
    this.hasNoPermissions$ = toObservable(this.hasNoPermissions);

    this.userProfileResource = this.homeService.getUserProfile();
    
    this.profileSettled = computed(() =>
      this.userProfileResource.hasValue() || this.userProfileResource.error() !== undefined);

    effect(() => {
      if (!this.userProfileResource.hasValue()) return;
      const response = this.userProfileResource.value();
      if (response && response.data) {
        this.userId.set(response.data.userId || 0);
        this.firstName.set(response.data.firstName);
        this.lastName.set(response.data.lastName);
        this.isActive.set(response.data.isActive);
        this.facilityIds.set(response.data.facilityIds ?? []);
        this.facilityPermissions.set(response.data.permissions ?? []);
        this.profileLoaded.set(true);
        this.authenticated.set(this.isActive());

        if (!this.isActive()) {
          this.router.navigate(['/not-authorized']);
        }
      }
    });
   }

  setUserInfo(account: any) {
    account?.name && this.name.set(account.name);
    account?.username && this.username.set(account.username);
    this.authenticated.set(!!account);
    this.http.get(this.userInfoUri).pipe(
      map((resp: any) => resp.data),
      catchError(() => of(null))
    ).subscribe(userInfo => {
      console.log('user info response');
      if (userInfo) {
        this.id.set(userInfo.userId);
        userInfo.displayName && this.displayName.set(userInfo.displayName)
        this.jobTitle.set(userInfo.jobTitle);
        this.name.set(this.formatName(userInfo.displayName));
      }
    });
    if (USE_BACKEND_AVATAR_API) {
      this.http.get(this.avatarUri).pipe(
        map((r: any) => r.data)
      ).subscribe(data => {
        if (data.imageData) {
          this.photo.set(`data:image/png;base64,${data.imageData}`);
        } else if (data.message) {
          console.log('avatar:', data.message);
        }
      });
    } else {
      this.graphService.getAvatar().pipe(
        map((res: ArrayBuffer) => new Blob([res], { type: 'image/jpeg' })),
        catchError(err => {
        if (err.status !== 404) {
          console.warn('Graph avatar fetch failed:', err.message);
        }
        return EMPTY;
      })
      ).subscribe(blob => {
        console.log(blob)
        this.photo.set(URL.createObjectURL(blob));
      });
    }
    this.graphService.groups
      .pipe(
        // transform groupIds to UserPermission constants
        map(groupIds => groupIds.map(userPermissionFromGroupId).filter(p => !!p)),
        map(perms => perms.length ? perms : [UserPermission.UNAUTHORIZED]),
        catchError(error => {
          console.error('Error fetching groups:', error);
          this.permissions.set([]); // Set permissions to empty in case of error
          return of([]);
        })
      )
      .subscribe(p => {
        this.permissions.set(p);
        this.permissionsLoaded.set(true);
      })
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/logged-out',
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  private formatName(displayName: string): string {
    return displayName.split(' ').map(name => name.charAt(0).toUpperCase() + name.slice(1)).join(' ');
  }
}
